import React, { useMemo } from 'react';
import { calculateSequence } from '../utils/pdfGenerator';
import SafeIcon from '../common/SafeIcon';
import { FiFile } from 'react-icons/fi';

const PreviewStrip = ({ images, settings }) => {
  const pages = useMemo(
    () => calculateSequence(images, settings),
    [images, settings]
  );

  if (pages.length === 0) return null;

  const getPageStyle = (pageItems) => {
    const { orientation } = settings;
    const base =
      'relative rounded-lg shadow-md border-2 overflow-hidden bg-white transition-all duration-500 ease-in-out ';
    const border = 'border-solid border-gray-200 ';

    let size = '';
    if (orientation === 'landscape') {
      size = 'w-48 h-32';
    } else if (orientation === 'portrait') {
      size = 'w-32 h-48';
    } else {
      const firstItem = pageItems[0];
      size = firstItem?.aspectRatio > 1 ? 'w-48 h-32' : 'w-32 h-48';
    }

    return base + border + size;
  };

  const getGridClass = (count) => {
    if (count <= 1) return 'grid-cols-1 grid-rows-1';
    if (count === 2) return 'grid-cols-1 grid-rows-2';
    if (count <= 4) return 'grid-cols-2 grid-rows-2';
    if (count <= 6) return 'grid-cols-2 grid-rows-3';
    if (count <= 8) return 'grid-cols-2 grid-rows-4';
    return 'grid-cols-3 grid-rows-4';
  };

  return (
    <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-sm">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h3 className="text-base font-black text-gray-800 uppercase tracking-tight">
            Layout Preview
          </h3>

          <div className="flex items-center gap-3 mt-2">
            <span
              className={`text-xs font-black px-3 py-1 rounded-full capitalize ${
                settings.orientation === 'auto'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-blue-100 text-blue-700'
              }`}
            >
              {settings.orientation} Mode
            </span>

            <span className="text-xs font-black px-3 py-1 rounded-full bg-gray-100 text-gray-600">
              {settings.pageSize}
            </span>

            <span className="text-xs font-black px-3 py-1 rounded-full bg-blue-50 text-blue-600">
              {settings.imagesPerPage} per page
            </span>
          </div>
        </div>

        <div className="text-right">
          <span className="text-sm font-black text-blue-600 bg-blue-50 px-4 py-2 rounded-xl border border-blue-100">
            {pages.length} PAGES TOTAL
          </span>
        </div>
      </div>

      <div className="flex gap-8 overflow-x-auto pb-8 custom-scrollbar items-end min-h-[220px] px-2">
        {pages.map((pageItems, index) => (
          <div
            key={`page-${index}`}
            className="flex flex-col items-center flex-shrink-0 group"
          >
            <span className="text-xs font-black text-gray-400 mb-3 group-hover:text-blue-500 transition-colors">
              PAGE {index + 1}
            </span>

            <div className={getPageStyle(pageItems)}>
              <div
                className={`grid w-full h-full gap-1 p-1 ${getGridClass(
                  pageItems.length
                )}`}
              >
                {pageItems.map((item, itemIndex) => (
                  <div
                    key={item.id || `item-${itemIndex}`}
                    className="relative w-full h-full rounded overflow-hidden flex flex-col"
                  >
                    {item.type === 'blank' ? (
                      <div className="w-full h-full flex flex-col items-center justify-center opacity-30">
                        <SafeIcon icon={FiFile} className="text-xl text-gray-300" />
                      </div>
                    ) : (
                      <>
                        <div
                          className="flex-1 relative overflow-hidden"
                          style={{
                            backgroundColor: settings.imageBackground,
                          }}
                        >
                          <img
                            src={item.dataUrl}
                            alt="preview"
                            className={`w-full h-full ${
                              settings.fitMode === 'fill'
                                ? 'object-cover'
                                : 'object-contain'
                            }`}
                          />
                        </div>

                        {settings.showDetails && (
                          <div className="h-4 flex items-center justify-center bg-gray-50 border-t border-gray-100 px-1">
                            <span className="text-[6px] font-bold text-gray-400 truncate uppercase">
                              {item.name}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>

              {settings.margin > 0 && (
                <div
                  className="absolute inset-0 border-2 border-blue-500/10 pointer-events-none"
                  style={{ margin: `${settings.margin / 4}px` }}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PreviewStrip;